// src/pages/auth/Login.js
import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { Box, Typography, TextField, Button, Checkbox, FormControlLabel, Link, Paper, Divider, InputAdornment, IconButton } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import GoogleAuthButton from './GoogleAuthButton';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { REDIRECT_FE_URLS } from '../../utils/Constants';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    general: '',
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = { email: '', password: '', general: '' };
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return !newErrors.email && !newErrors.password;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const result = await login({ ...formData, rememberMe });

      if (result.success) {
        if (result.redirect_url === 'complete_onboarding') {
          navigate(REDIRECT_FE_URLS.complete_onboarding);
        } else if (result.redirect_url === 'account_status') {
          navigate(`${REDIRECT_FE_URLS.account_status}/${result.account_id}`, {
            state: { message: result.message },
          });
        } else if (result.redirect_url === 'app_download') {
          navigate(REDIRECT_FE_URLS.app_download, {
            state: { message: result.message },
          });
        } else {
          const from = location.state?.from;
          const redirectUrl = from ? from.pathname : REDIRECT_FE_URLS[result.redirect_url] || '/dashboard';
          navigate(redirectUrl);
        }
      } else {
        setErrors({ ...errors, general: result.error || 'Invalid email or password' });
      }
    } catch (error) {
      setErrors({ ...errors, general: 'Failed to login. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const inputHeight = 50;
  const fieldSx = (fieldError) => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#fff',
      minHeight: inputHeight,
      '& fieldset': {
        borderColor: fieldError ? '#d32f2f' : '#e0e0e0',
        borderWidth: fieldError ? 2 : 1,
        borderRadius: '8px',
      },
      '&:hover fieldset': {
        borderColor: fieldError ? '#d32f2f' : '#bdbdbd',
      },
      '&.Mui-focused fieldset': {
        borderColor: fieldError ? '#d32f2f' : '#059669',
        borderWidth: 2,
      },
    },
    '& .MuiFormHelperText-root': { marginLeft: 0 },
  });

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: 520,
        minWidth: 320,
        mx: 'auto',
        p: { xs: 2, sm: 2.5 },
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'rgba(0,0,0,0.08)',
        backgroundColor: '#fff',
        boxShadow: '0 2px 24px rgba(0,0,0,0.06)',
      }}
    >
      <Typography
        variant="h6"
        component="h1"
        align="center"
        sx={{
          fontWeight: 700,
          color: '#1f2937',
          fontSize: '1.25rem',
          mb: 0.5,
        }}
      >
        Welcome!
      </Typography>
      <Typography variant="body1" align="center" sx={{ color: '#6b7280', fontSize: '0.9375rem', mb: 1.5 }}>
        Log in to access your account.
      </Typography>

      {errors.general && (
        <Box
          sx={{
            backgroundColor: '#FEF2F2',
            color: '#D32F2F',
            p: 1.25,
            borderRadius: '8px',
            mb: 1.5,
            fontSize: '0.875rem',
          }}
        >
          {errors.general}
        </Box>
      )}

      <GoogleAuthButton action="login" />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
        <Divider sx={{ flex: 1, borderColor: '#e0e0e0' }} />
        <Typography variant="body2" sx={{ color: '#9e9e9e', fontWeight: 600, letterSpacing: 0.5 }}>
          OR
        </Typography>
        <Divider sx={{ flex: 1, borderColor: '#e0e0e0' }} />
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
        <TextField
          required
          fullWidth
          id="email"
          label="Email"
          name="email"
          placeholder="Email*"
          type="email"
          autoComplete="email"
          autoFocus
          size="medium"
          value={formData.email}
          onChange={handleChange}
          error={!!errors.email}
          helperText={errors.email}
          sx={{ mb: 1.5, ...fieldSx(errors.email) }}
          variant="outlined"
        />

        <TextField
          required
          fullWidth
          name="password"
          label="Password"
          placeholder="Password*"
          type={showPassword ? 'text' : 'password'}
          id="password"
          size="medium"
          autoComplete="current-password"
          value={formData.password}
          onChange={handleChange}
          error={!!errors.password}
          helperText={errors.password}
          sx={{ mb: 1.5, ...fieldSx(errors.password) }}
          variant="outlined"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword((show) => !show)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                size="small"
                sx={{ color: '#757575', '&.Mui-checked': { color: '#059669' } }}
              />
            }
            label={<Typography variant="body2" sx={{ color: '#424242', fontSize: '0.875rem' }}>Remember</Typography>}
          />
          <Link
            component={RouterLink}
            to="/auth/forgot-password"
            underline="none"
            sx={{ color: '#3B82F6', fontWeight: 600, fontSize: '0.875rem', '&:hover': { textDecoration: 'underline' } }}
          >
            Forgot Password?
          </Link>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            minHeight: 52,
            py: 1.5,
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: 700,
            textTransform: 'none',
            background: 'linear-gradient(to right, #34D399 0%, #059669 100%)',
            boxShadow: '0 2px 8px rgba(5, 150, 105, 0.3)',
            '&:hover': {
              background: 'linear-gradient(to right, #2dd4a0 0%, #047857 100%)',
              boxShadow: '0 4px 12px rgba(5, 150, 105, 0.35)',
            },
            '&:disabled': { background: '#bdbdbd', color: '#fff' },
          }}
        >
          {loading ? 'Logging in...' : 'Login'}
        </Button>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#4b5563', fontSize: '0.875rem' }}>
            No account yet?{' '}
            <Link
              component={RouterLink}
              to="/auth/signup"
              underline="none"
              sx={{ color: '#3B82F6', fontWeight: 700, fontSize: '0.875rem', '&:hover': { textDecoration: 'underline' } }}
            >
              Sign up
            </Link>
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default Login;